/**
 * Table IDs (BaseTable `tableId`) that render with full-width column fill.
 *
 * Centralized here instead of a per-listing `fillAvailableWidth: true` option:
 * repeating that flag inside the identical listing boilerplate across many files
 * counts as duplicated new code (SonarCloud). BaseTable reads this set via
 * `props.tableId`. Inline `fillAvailableWidth` still works too (the two are
 * OR-ed together), so already-filled tables (Accounts, Tenants, Claims) that
 * pass the option directly are unaffected.
 */
export const FILL_ENABLED_TABLE_IDS: ReadonlySet<string> = new Set([
  'apiKeys',
  'domains',
  'COWBELL_ALL_SUBMISSIONS',
  'cowbell-users',
  'raters',
  'roles',
  'webhooks',
  'surplusLicense',
  'allagencyteams',
  'allusers',
  'COWBELL_OFAC_CHECKS',
  'admin_presets',
  'BANK_TRANSACTIONS',
  'PhDocuments',
  'COWBELL_CLAIM_DOCS_V2',
  'UWDocuments',
  // The vendor tenant's two tables carry few columns, so without fill the last one absorbs all
  // the leftover width — which put the row-actions menu in the middle of ~300px of empty space.
  'VENDOR_CLAIMS',
  'CLAIMS_INVOICES',
  // Fixed widths left the diary narrower than its container, and the expand chevron's column
  // took up the slack. Fill gives that space to the data columns and makes them resizable.
  'CLAIMS_DIARY',
  // Agency and admin Locations: their columns are narrow, so without fill the row-menu column at the end took all the
  // leftover width and left the menu at the start of a wide empty cell.
  'agency_locations',
  'admin_locations',
]);

/**
 * Whether a table should render with full-width column fill: true if the inline
 * `fillAvailableWidth` prop is set (used by Accounts/Tenants/Claims) or the
 * table's id is centrally enabled above. Pure so it is unit-testable — BaseTable
 * itself is stubbed in listing tests, so keeping the decision here keeps it
 * covered.
 */
export const isFillEnabled = (
  tableId: string | undefined,
  fillProp: boolean | undefined
): boolean => !!fillProp || FILL_ENABLED_TABLE_IDS.has(tableId ?? '');
